import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Radio, FileText, AlertCircle, Phone, Plus, ArrowRight, Loader2, Megaphone } from 'lucide-react';
import dayjs from 'dayjs';
import { api } from '../apiClient';
import { useAuth } from '../AuthContext';

const STATUS = {
  pending: { bg: 'bg-amber-50', fg: 'text-amber-600', label: 'PENDING' },
  contacted: { bg: 'bg-pink-50', fg: 'text-pink-700', label: 'CONTACTED' },
  closed: { bg: 'bg-emerald-50', fg: 'text-emerald-600', label: 'CLOSED' },
};

function StatCard({ label, value, icon: Icon, tint, fg, testId }) {
  return (
    <div data-testid={testId} className="w-[calc(50%-6px)] bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col gap-1.5">
      <div className={`w-9 h-9 rounded-xl ${tint} flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${fg}`} />
      </div>
      <span className="text-2xl font-black text-gray-900">{value}</span>
      <span className="text-xs font-bold text-gray-500">{label}</span>
    </div>
  );
}

export default function OrgDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/organizer/dashboard');
      setData(r.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const orgName = user?.organizer_profile?.organization_name || user?.full_name || 'Organizer';

  return (
    <div className="max-w-5xl mx-auto px-6 py-4" data-testid="org-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-500">Welcome back</p>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight" data-testid="org-dashboard-orgname">{orgName}</h1>
        </div>
        <div className="w-10 h-10 rounded-full bg-brand-magenta flex items-center justify-center">
          <Megaphone className="w-5 h-5 text-white" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-magenta" /></div>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mt-4">
            <StatCard label="Total Events" value={data?.total_events ?? 0} icon={CalendarDays} tint="bg-pink-50" fg="text-brand-magenta" testId="stat-total-events" />
            <StatCard label="Live" value={data?.live_events ?? 0} icon={Radio} tint="bg-emerald-50" fg="text-emerald-600" testId="stat-live-events" />
            <StatCard label="Drafts" value={data?.draft_events ?? 0} icon={FileText} tint="bg-amber-50" fg="text-amber-600" testId="stat-draft-events" />
            <StatCard label="Pending Enq." value={data?.pending_enquiries ?? 0} icon={AlertCircle} tint="bg-red-50" fg="text-red-500" testId="stat-pending-enquiries" />
          </div>

          <div className="flex items-center justify-between brand-gradient rounded-2xl p-5 mt-4" data-testid="stat-week-enquiries">
            <div>
              <p className="text-pink-200 text-xs font-bold tracking-wide">This week's enquiries</p>
              <p className="text-white text-3xl font-black">{data?.total_enquiries_this_week ?? 0}</p>
            </div>
            <button
              data-testid="dashboard-view-enquiries-btn"
              onClick={() => navigate('/organizer/enquiries')}
              className="flex items-center gap-1 bg-white text-brand-magenta px-3 py-2 rounded-full text-xs font-extrabold hover:bg-pink-50 transition-colors"
            >
              View all <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <h2 className="text-lg font-black text-gray-900 mt-6 mb-3">Recent Enquiries</h2>

          {(data?.recent_enquiries || []).length === 0 ? (
            <div className="flex flex-col items-center py-10 text-gray-400 gap-2">
              <MessageSquareIcon className="w-10 h-10" />
              <p className="text-sm text-gray-500 text-center">No enquiries yet. Publish an event to start receiving them.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {(data?.recent_enquiries || []).map((e) => {
                const s = STATUS[e.status] || STATUS.pending;
                return (
                  <button
                    key={e.id}
                    data-testid={`dashboard-recent-${e.id}`}
                    onClick={() => navigate('/organizer/enquiries')}
                    className="flex items-center gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <img src={e.event_snapshot.banner_url} alt="" className="w-14 h-14 rounded-xl object-cover bg-gray-100 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-extrabold text-gray-900 truncate">{e.event_snapshot.title}</p>
                      <p className="text-xs text-gray-500">{e.name} · {e.guests_count} guest{e.guests_count > 1 ? 's' : ''}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`${s.bg} ${s.fg} text-[9px] font-black tracking-wide px-2 py-0.5 rounded-full`}>{s.label}</span>
                        <span className="text-[11px] text-gray-400">{dayjs(e.created_at).format('MMM D · h:mm A')}</span>
                      </div>
                    </div>
                    <a
                      data-testid={`dashboard-call-${e.id}`}
                      href={`tel:${e.phone}`}
                      onClick={(ev) => ev.stopPropagation()}
                      className="w-9 h-9 rounded-full bg-brand-magenta flex items-center justify-center flex-shrink-0 hover:bg-pink-700 transition-colors"
                    >
                      <Phone className="w-4 h-4 text-white" />
                    </a>
                  </button>
                );
              })}
            </div>
          )}

          <button
            data-testid="dashboard-create-event-btn"
            onClick={() => navigate('/organizer/events/new')}
            className="w-full mt-6 flex items-center justify-center gap-2 bg-brand-magenta hover:bg-pink-700 text-white py-3.5 rounded-2xl font-black text-[15px] transition-colors"
          >
            <Plus className="w-5 h-5" /> Create New Event
          </button>
        </>
      )}
    </div>
  );
}

function MessageSquareIcon({ className }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
}
